import { getEnv } from '../../getEnv';
import { getRefreshToken } from './sessionStore';
import { logger } from '../../lib/logger';

// ─── PKCE storage key ─────────────────────────────────────────────────────────

const PKCE_VERIFIER_KEY = 'pkce_verifier';

// ─── PKCE diagnostic trace (TEMPORARY) ─────────────────────────────────────────
// Paper trail for the PKCE mismatch investigation. Logs enough to tell apart
// "verifier never existed" (customer bypassed /signin) from "verifier existed
// but didn't match what Keycloak expected" (a real bug in this flow), without
// putting the full verifier/challenge — which are effectively bearer secrets
// for the token exchange — into console/log aggregation. Only the first 8
// chars + length are logged, which is enough to spot mismatches/overwrites by
// eye without being usable to complete the flow.
// Remove this block once the root cause is confirmed and PKCE is re-enabled.
const pkceTrace = (event, data = {}) => {
  // console.info('[PKCE trace]', event, {
  //   ts: new Date().toISOString(),
  //   href: window.location.href,
  //   referrer: document.referrer || null,
  //   ...data,
  // });
};

const shortHash = (value) =>
  value ? `${value.slice(0, 8)}… (len ${value.length})` : null;

// ─── PKCE toggle (TEMPORARY) ───────────────────────────────────────────────────
// Some customer IDP login links point straight at Keycloak instead of the
// portal's /signin, so generateAuthUrl() below never runs for them and no
// PKCE verifier/challenge is ever generated. Keycloak still expects a
// matching code_challenge on the authorization request, so those logins
// fail with a PKCE mismatch on Keycloak's own sign-in screen.
//
// Disabling PKCE here (VITE_SKIP_PKCE=true) while we audit and correct every
// customer's IDP login path to route through the portal first.
//
// IMPORTANT: this only stops the portal from sending/expecting PKCE params.
// If Keycloak's client has "Proof Key for Code Exchange Code Challenge
// Method" set to S256 (required), it will still reject authorization
// requests that arrive without a code_challenge — including the
// direct-to-Keycloak links this is meant to work around. That side needs
// the Keycloak client's PKCE requirement relaxed to "not required" for the
// duration of this workaround, or those direct links fixed to go through
// /signin first.
//
// Revert: set VITE_SKIP_PKCE back to false/unset once all IDP paths are
// confirmed to hit /signin first. Do not delete the PKCE helpers below.
const SKIP_PKCE = import.meta.env.VITE_SKIP_PKCE === 'true';

// ─── PKCE helpers ─────────────────────────────────────────────────────────────

const base64urlEncode = (buffer) =>
  btoa(String.fromCharCode(...new Uint8Array(buffer)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const generateVerifier = () => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return base64urlEncode(array.buffer);
};

const generateChallenge = async (verifier) => {
  const hash = await crypto.subtle.digest(
    'SHA-256',
    new TextEncoder().encode(verifier)
  );
  return base64urlEncode(hash);
};

// ─── Proxy helper (unchanged) ─────────────────────────────────────────────────
// Only used for background API calls (token exchange, revocation)
// NOT used for browser redirects (authorization, end session)

const toProxiedUrl = (absoluteUrl, keycloakOrigin) => {
  if (!absoluteUrl) return absoluteUrl;
  return absoluteUrl.replace(keycloakOrigin, `${window.location.origin}/auth`);
};

// ─── OIDC discovery cache ─────────────────────────────────────────────────────

let wellKnownCache = null;

const getWellKnown = async () => {
  if (wellKnownCache) return wellKnownCache;

  const env = getEnv();
  const url = `/auth/realms/${env.keycloak_realm}/.well-known/openid-configuration`;

  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`Discovery fetch failed: ${r.status}`);
    wellKnownCache = await r.json();
  } catch (err) {
    logger.error('Failed to fetch OIDC discovery document');
    throw err;
  }

  if (!wellKnownCache.authorization_endpoint || !wellKnownCache.token_endpoint) {
    throw new Error('OIDC discovery document is missing required endpoints');
  }

  return wellKnownCache;
};

// ─── generateAuthUrl ──────────────────────────────────────────────────────────
// Replaces client.code.getUri()
// Generates PKCE verifier/challenge, stores verifier in sessionStorage,
// and returns the full authorization URL for browser redirect.
// (PKCE step skipped entirely while SKIP_PKCE is true — see toggle above.)

export const generateAuthUrl = async (extraParams = {}) => {
  const env = getEnv();
  const wellKnown = await getWellKnown();

  const params = new URLSearchParams({
    response_type:  'code',
    client_id:      env.oauth_client_id,
    redirect_uri:   env.oauth_redirect_uri,
    scope:          'openid',
    ...extraParams,
  });

  if (!SKIP_PKCE) {
    const verifier = generateVerifier();
    const challenge = await generateChallenge(verifier);

    const existing = sessionStorage.getItem(PKCE_VERIFIER_KEY);
    if (existing) {
      // A verifier is already sitting in sessionStorage — this generateAuthUrl
      // call is about to overwrite it. If a login attempt from an earlier call
      // is still in flight (double-click, back button, second tab on this
      // origin), that earlier authorization code will no longer have a
      // matching verifier by the time it reaches exchangeCodeForToken below.
      pkceTrace('verifier_overwritten', {
        previousVerifier: shortHash(existing),
        newVerifier: shortHash(verifier),
      });
    }

    sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
    params.set('code_challenge', challenge);
    params.set('code_challenge_method', 'S256');

    pkceTrace('auth_url_generated', {
      verifier: shortHash(verifier),
      challenge: shortHash(challenge),
    });
  } else {
    pkceTrace('auth_url_generated_pkce_skipped');
  }

  return `${wellKnown.authorization_endpoint}?${params.toString()}`;
};

// ─── exchangeCodeForToken ─────────────────────────────────────────────────────
// Replaces client.code.getToken()
// Extracts the auth code from the callback URL, retrieves the stored PKCE
// verifier, and POSTs to the token endpoint.
// Returns only { access_token, refresh_token, id_token } — discards the
// remaining Keycloak response fields (expires_in, token_type, scope, etc.)
// which are either derivable from the JWT or not needed by the portal.
// (PKCE verifier lookup/send skipped entirely while SKIP_PKCE is true.)

export const exchangeCodeForToken = async (callbackUrl) => {
  const env = getEnv();
  const wellKnown = await getWellKnown();
  const keycloakOrigin = new URL(wellKnown.token_endpoint).origin;
  const tokenUrl = toProxiedUrl(wellKnown.token_endpoint, keycloakOrigin);

  const code = new URL(callbackUrl).searchParams.get('code');
  if (!code) throw new Error('No authorization code in callback URL');

  const body = {
    grant_type:    'authorization_code',
    client_id:     env.oauth_client_id,
    redirect_uri:  env.oauth_redirect_uri,
    code,
  };

  if (!SKIP_PKCE) {
    const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);

    if (!verifier) {
      // No verifier in sessionStorage at all — this login never went through
      // generateAuthUrl() in this browser session (e.g. entered via a direct
      // Keycloak/IDP link instead of the portal's /signin), or the tab/session
      // storage didn't survive the redirect chain through the SAML broker.
      pkceTrace('exchange_missing_verifier', { code: shortHash(code) });
      throw new Error('No PKCE verifier found — possible session issue');
    }

    // Recompute the challenge from the stored verifier purely for comparison
    // against what was logged at auth_url_generated time — if they don't
    // match, the value in sessionStorage isn't the one this code was
    // challenged with (e.g. overwritten by a second concurrent attempt).
    const recomputedChallenge = await generateChallenge(verifier);
    pkceTrace('exchange_attempt', {
      code: shortHash(code),
      verifier: shortHash(verifier),
      recomputedChallenge: shortHash(recomputedChallenge),
    });

    sessionStorage.removeItem(PKCE_VERIFIER_KEY);
    body.code_verifier = verifier;
  } else {
    pkceTrace('exchange_attempt_pkce_skipped', { code: shortHash(code) });
  }

  const r = await fetch(tokenUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams(body),
  });

  if (!r.ok) {
    const body = await r.json().catch(() => ({}));
    pkceTrace('exchange_failed', {
      status: r.status,
      error: body.error,
      error_description: body.error_description,
    });
    throw new Error(body.error_description ?? `Token exchange failed: ${r.status}`);
  }

  pkceTrace('exchange_succeeded');

  const { access_token, refresh_token, id_token } = await r.json();
  return { access_token, refresh_token, id_token };
};

// ─── buildEndSessionUrl ───────────────────────────────────────────────────────
// Replaces client.endSessionUrl()
// Builds the Keycloak end-session URL for browser redirect.
// id_token_hint is intentionally omitted — it contains PII and is not
// required since a single shared client is used across all environments.

export const buildEndSessionUrl = async (queryParams = {}) => {
  const env = getEnv();
  const wellKnown = await getWellKnown();

  const endSessionEndpoint = wellKnown.end_session_endpoint;
  if (!endSessionEndpoint) {
    throw new Error('end_session_endpoint not found in discovery document');
  }

  const url = new URL(endSessionEndpoint);
  url.searchParams.set('client_id', env.oauth_client_id);
  for (const [key, value] of Object.entries(queryParams)) {
    if (value != null) url.searchParams.set(key, value);
  }
  return url.toString();
};

// ─── refreshToken (unchanged) ─────────────────────────────────────────────────

const REFRESH_TIMEOUT_MS = 10000;
let refreshInFlight = null;

export const refreshToken = async () => {
  if (refreshInFlight) {
    return refreshInFlight;
  }

  const refresh_token = getRefreshToken() || '';
  const env = getEnv();
  const refresh_url = `/auth/realms/${env.keycloak_realm}/protocol/openid-connect/token`;

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), REFRESH_TIMEOUT_MS);

  refreshInFlight = fetch(refresh_url, {
    method:    'POST',
    keepalive: !import.meta.env.DEV,
    signal:    controller.signal,
    body: new URLSearchParams({
      client_id:     env.oauth_client_id,
      grant_type:    'refresh_token',
      refresh_token,
    }),
  })
    .then(async r => {
      if (!r.ok) {
        try {
          const errorBody = await r.json();
          return errorBody;
        } catch {
          return { error: 'http_error', error_description: `HTTP ${r.status} ${r.statusText}` };
        }
      }
      return r.json();
    })
    .catch(err => {
      if (err.name === 'AbortError') {
        logger.error('Token refresh request timed out');
        return { error: 'timeout', error_description: 'Refresh request exceeded timeout' };
      }
      throw err;
    })
    .finally(() => {
      clearTimeout(timeout);
      refreshInFlight = null;
    });

  return refreshInFlight;
};
